// NodeDateTimeInput.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
  DEFAULT_MINUTE_STEP,
  applyTimeToDate,
  generateTimeOptions,
  getNearestSlotValue,
  isCompleteRawDate,
  toTimeKey,
} from 'node/form/fields/utilities/dateFieldUtils';
import { normalizeDateValue, timestampFromDate } from 'utilities/DateTime';

// CSS
import './NodeDateTimeInput.css';

// Components
import DatePicker from 'components/datepicker/DatePicker';
import SelectInput from 'components/selectinput/SelectInput';

/**
 * NodeDateTimeInput
 *
 * Shared datetime input for schema-driven date/time fields.
 *
 * @param {object} value - Current field value.
 * @param {Function} onChange - Callback when value changes.
 * @param {boolean} hybrid - Whether to use hybrid DatePicker mode.
 * @param {number} minuteStep - Minute increment for time options.
 *
 * @returns {ReactNode} The rendered component.
 */
const NodeDateTimeInput = ({
  value,
  onChange,
  hybrid = true,
  minuteStep = DEFAULT_MINUTE_STEP,
}) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // ----- STATE ----- //

  const [currentDate, setCurrentDate] = useState(null);
  const [timeValue, setTimeValue] = useState('');
  const [isInvalid, setIsInvalid] = useState(false);

  // ----- REFS ----- //

  const rawValueRef = useRef('');

  // ----- MEMOS ----- //

  const timeOptions = useMemo(() => generateTimeOptions(minuteStep), [minuteStep]);

  // ----- UTILITIES ----- //

  /**
   * Builds the full datetime value object from the selected date and time.
   * 
   * @param {Date} date - The selected date.
   * 
   * @returns {object} The complete datetime value object to be emitted.
   */

  const buildDateTimeValue = (date) => {
    if (!date) return null;
    return {
      startDate: timestampFromDate(date),
      startPrecision: 'time',
      endDate: null,
      endPrecision: null,
      isRange: false,
    };
  };

  /**
   * Emits the change event with the updated datetime value.
   * 
   * @param {Date} nextDate - The newly selected date to emit.
   */
  const emitChange = (nextDate) => {
    if (typeof onChange !== 'function') return;
    onChange(buildDateTimeValue(nextDate));
  };

  // ----- EFFECTS ----- //

  // Syncs with external value changes, resolving time to nearest slot if needed

  useEffect(() => {

    // Normalize the incoming date value
    const normalizedDate = normalizeDateValue(value?.startDate);
    setCurrentDate(normalizedDate);

    if (normalizedDate) {
      const exactTime = toTimeKey(normalizedDate);
      const resolvedTime = timeOptions.some((opt) => opt.value === exactTime)
        ? exactTime
        : getNearestSlotValue(normalizedDate, timeOptions, minuteStep);
      setTimeValue(resolvedTime);
    } else {
      setTimeValue('');
    }
  }, [value, timeOptions, minuteStep]);

  // ----- HANDLERS ----- //

  /**
   * Handles raw input changes from the DatePicker, updating the invalid state accordingly.
   * 
   * @param {string} rawValue - The raw input value from the DatePicker.
   */

  const handleRawChange = (rawValue) => {

    // Store the raw value in a ref to check for completeness without causing re-renders
    rawValueRef.current = rawValue || '';

    // If the raw value is cleared, reset invalid state
    if (!isCompleteRawDate(rawValue)) {
      setIsInvalid(false);
    }
  };

  /**
   * Handles changes from the DatePicker, updating state and emitting changes as needed.
   * 
   * @param {Date} nextDate - The newly selected date from the DatePicker.
   */
  const handleDateChange = (nextDate) => {

    // Clear invalid state on any date change
    setCurrentDate(nextDate);

    // If no date is selected, clear the time and emit a null value
    if (nextDate) {
      setIsInvalid(false);
      const nextTime = timeValue || getNearestSlotValue(nextDate, timeOptions, minuteStep);
      setTimeValue(nextTime);
      emitChange(applyTimeToDate(nextDate, nextTime));
      return;
    }

    // If date is cleared, reset time and emit null
    const rawValue = rawValueRef.current || '';
    if (!rawValue) {
      setIsInvalid(false);
      setTimeValue('');
      emitChange(null);
      return;
    }

    // If there is still raw input that isn't a complete date, mark as invalid
    if (isCompleteRawDate(rawValue)) {
      setIsInvalid(true);
    }
  };

  /**
 * Handles date blur.
 */
  const handleDateBlur = ({ rawValue }) => {
    if (currentDate) return;

    if (!isCompleteRawDate(rawValue)) {
      setIsInvalid(false);
      setTimeValue('');
      emitChange(null);
    }
  };

  /**
   * Handles changes to the time select input, applying the selected time to the current 
   * date and emitting the change.
   * 
   * @param {Event} event - The change event from the time select input.
   */
  const handleTimeChange = (event) => {
    const nextTime = event.target.value;
    setTimeValue(nextTime);
    if (!currentDate) return;
    emitChange(applyTimeToDate(currentDate, nextTime));
  };

  // Determine the time value to display, preferring the current timeValue state but falling back
  const resolvedTimeValue = timeValue || getNearestSlotValue(currentDate, timeOptions, minuteStep);

  // ----- RENDER ----- //

  return (

    <div
      className="node-date-time-input-container"
      style={{
        gap: hybrid ? 0 : 12,
      }}
    >

      <DatePicker
        value={currentDate}
        inputClassName={isInvalid ? 'date-picker-invalid' : ''}
        startIconKey="calendar_month"
        startIconColor={theme.foregroundMuted}
        iconSize={22}
        hybrid={hybrid}
        onBlur={handleDateBlur}
        onChange={handleDateChange}
        onRawChange={handleRawChange}
      />

      {/* TIME SELECT */}
      {currentDate && (

        <div className="node-date-time-input-time">

          <SelectInput
            value={resolvedTimeValue}
            options={timeOptions}
            hybrid={hybrid}
            onChange={handleTimeChange}
          />

        </div>

      )}
    </div>
  );
};

// Dev Tools
NodeDateTimeInput.displayName = 'NodeDateTimeInput';

export default NodeDateTimeInput;
